import { gql } from '@apollo/client';
import React from 'react';
import { useDispatch } from 'react-redux';
import useAppSelector from '../app/redux/useAppSelector';
import AvatarStack from '../components/AvatarStack';
import Badge from '../components/Badge';
import { MenuItem } from '../components/Menu';
import type { GroupUserCard_MemberFragment } from '../graphql/generated/graphqlTypes';
import { GroupUserRole } from '../graphql/generated/graphqlTypes';
import UIActions from '../ui/UIActions';
import AdminBadge from '../user/AdminBadge';
import UserBadge from '../user/profile/UserBadge';
import userPath from '../utils/url/userPath';
import GroupChildCard from './GroupChildCard';
import styles from './GroupUserCard.module.scss';

const GROUP_USER_CARD_FRAGMENT = gql`
  fragment GroupUserCard_member on User {
    id
    name
    title
    type
    ...AvatarStack_users
    ...UserBadge_user
  }
  ${AvatarStack.fragments.users}
  ${UserBadge.fragments.user}
`;

interface GroupUserCardProps {
  user: GroupUserCard_MemberFragment;
  owner?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChangeRole?: (role: GroupUserRole) => Promise<any>;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRemove?: () => Promise<any>;
}

const GroupUserCard = ({
  user,
  owner = false,
  onChangeRole,
  onRemove,
}: GroupUserCardProps) => {
  const dispatch = useDispatch();
  const { name, title } = user;
  const isSelf = useAppSelector((state) => state.session.user.id === user.id);

  const showToast = (message: string) => {
    dispatch(
      UIActions.pushToast({
        level: 'success',
        message,
      }),
    );
  };

  return (
    <GroupChildCard
      to={userPath(user)}
      avatars={<AvatarStack users={[user]} />}
      badge={owner && <Badge>Owner</Badge>}
      title={
        <div className={styles.title}>
          <div className={styles.name}>{name}</div>
          {user.type && ['GUEST', 'BOT'].includes(user.type) && (
            <UserBadge user={user} inline={false} />
          )}
        </div>
      }
      description={title}
      menuItems={
        (onChangeRole || onRemove) && (
          <>
            {onChangeRole &&
              (owner ? (
                <MenuItem
                  onClick={() =>
                    onChangeRole(GroupUserRole.Member).then(() =>
                      showToast(
                        `${name} has been demoted to a standard member.`,
                      ),
                    )
                  }
                >
                  {isSelf ? 'Demote yourself' : 'Demote to member'}
                </MenuItem>
              ) : (
                <MenuItem
                  onClick={() =>
                    onChangeRole(GroupUserRole.Owner).then(() =>
                      showToast(`${name} has been promoted to an owner.`),
                    )
                  }
                  right={!owner && isSelf ? AdminBadge : undefined}
                >
                  {isSelf ? 'Become owner' : 'Promote to owner'}
                </MenuItem>
              ))}
            {onRemove && (
              <MenuItem
                danger
                onClick={() =>
                  onRemove().then(() =>
                    showToast(`${name} has been removed from the group.`),
                  )
                }
              >
                {isSelf ? 'Leave group' : 'Remove from group'}
              </MenuItem>
            )}
          </>
        )
      }
    />
  );
};

GroupUserCard.fragments = {
  member: GROUP_USER_CARD_FRAGMENT,
};

export default GroupUserCard;
